import type { FormNode } from '../../designer/types/form.types'

export function isPagesNode(n: FormNode): boolean {
  return n.type === 'Pages'
}

export function pageChildren(pages: FormNode): FormNode[] {
  return (pages.children ?? []).filter((c) => c.type === 'Page')
}

/** Badge counts: page name → tabular row count when the page has exactly one Table. */
export function pageRowCountsFromData(
  root: FormNode[] | undefined,
  data: Record<string, unknown> | undefined | null,
): Record<string, number> {
  const counts: Record<string, number> = {}
  if (!root) return counts
  const values = data ?? {}
  const visit = (nodes: FormNode[]) => {
    nodes = wrapSiblingTablesAsPages(nodes)
    for (const n of nodes) {
      if (n.type === 'Pages') {
        for (const page of n.children ?? []) {
          if (page.type !== 'Page') continue
          const kids = page.children ?? []
          if (kids.length !== 1 || kids[0].type !== 'Table') continue
          const tableName = kids[0].name
          const rows = values[tableName]
          counts[page.name] = Array.isArray(rows) ? rows.length : 0
        }
      }
      if (n.children?.length) visit(n.children)
    }
  }
  visit(root)
  return counts
}

export function validatePagesTree(node: FormNode): string[] {
  const errors: string[] = []
  if (node.type !== 'Pages') {
    return errors
  }
  const kids = node.children ?? []
  if (kids.some((c) => c.type !== 'Page')) {
    errors.push('Pages may only contain Page nodes')
  }
  const hasNested = (n: FormNode): boolean => {
    for (const c of n.children ?? []) {
      if (c.type === 'Pages') return true
      if (hasNested(c)) return true
    }
    return false
  }
  if (hasNested(node)) {
    errors.push('Nested Pages are not allowed')
  }
  return errors
}

function pageNode(name: string, title: string, children: FormNode[]): FormNode {
  return {
    id: `page-${name}`,
    type: 'Page',
    name,
    title,
    row: 0,
    col: 0,
    colSpan: 12,
    width: 'auto',
    height: 'auto',
    visible: true,
    enabled: true,
    titleVisible: true,
    titleWidth: 'auto',
    style: {},
    events: {},
    children,
  }
}

/**
 * Custom/legacy item forms stack every Table as a sibling. Two or more
 * sibling Tables become one Pages strip (one Page per part) — same shape
 * ItemFormGenerator already emits.
 */
/** FormGrid sorts by `row`. Pages/Table at 0 with Header at 1 paint the grid first. */
function isGridNode(n: FormNode): boolean {
  return n.type === 'Pages' || n.type === 'Table'
}

/**
 * Header = nodes authored above the first Table/Pages (smaller row), plus a
 * Group/CommandBar that sits *before* the grid in the tree (legacy generator:
 * Header row 1, Pages row 0). Footer fields (Comment below ТЧ) stay out.
 */
function headerBandAboveGrid(nodes: FormNode[]): number {
  const grids = nodes.filter(isGridNode)
  if (grids.length === 0) return -1
  const minGrid = grids.reduce((m, n) => Math.min(m, n.row), grids[0].row)
  const firstGrid = nodes.findIndex(isGridNode)
  let band = -1
  for (let i = 0; i < nodes.length; i++) {
    const n = nodes[i]
    if (isGridNode(n) || n.type === 'Totals' || n.type === 'Page') continue
    if (n.row < minGrid) {
      band = Math.max(band, n.row)
      continue
    }
    if (i < firstGrid && (n.type === 'Group' || n.type === 'CommandBar')) {
      band = Math.max(band, n.row)
    }
  }
  return band
}

function placePagesBelowHeader(nodes: FormNode[]): FormNode[] {
  const headerBand = headerBandAboveGrid(nodes)
  if (headerBand < 0) return nodes
  return nodes.map((n) => {
    if (isGridNode(n) && n.row <= headerBand) {
      return { ...n, row: headerBand + 1 }
    }
    return n
  })
}

export function wrapSiblingTablesAsPages(nodes: FormNode[]): FormNode[] {
  const mapped = nodes.map((n) => {
    if (n.type === 'Pages' || !n.children?.length) return n
    return { ...n, children: wrapSiblingTablesAsPages(n.children) }
  })
  const tables = mapped.filter((n) => n.type === 'Table')
  if (tables.length < 2) return placePagesBelowHeader(mapped)
  const headerBand = headerBandAboveGrid(mapped)
  const tableRow = tables.reduce((m, t) => Math.max(m, t.row), 0)
  const pages: FormNode = {
    ...pageNode(
      'Pages',
      '',
      tables.map((t) =>
        pageNode(t.name, t.title || t.name, [{ ...t, titleVisible: false }]),
      ),
    ),
    id: 'pages-auto-tabular',
    type: 'Pages',
    titleVisible: false,
    row: Math.max(tableRow, headerBand + 1),
    col: 0,
    colSpan: 12,
  }
  const out: FormNode[] = []
  let inserted = false
  for (const n of mapped) {
    if (n.type !== 'Table') {
      out.push(n)
      continue
    }
    if (!inserted) {
      out.push(pages)
      inserted = true
    }
  }
  return placePagesBelowHeader(out)
}

export function appendJournalPage(root: FormNode[], journalGroup: FormNode): FormNode[] {
  const idx = root.findIndex((n) => n.type === 'Pages')
  if (idx < 0) return root
  const pages = root[idx]
  const journalPage = pageNode('JournalPreview', 'Journal', [journalGroup])
  return root.map((n, i) =>
    i !== idx ? n : { ...n, children: [...(pages.children ?? []), journalPage] },
  )
}
